// src/components/Sidebar.jsx

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Badge } from 'reactstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ORDERS_HISTORY, RESET_ORDERS_PAGINATION } from 'redux/actions/types';
import { logout } from 'redux/actions/user';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
// import IconButton from '@mui/material/IconButton';
import { useSocket } from 'providers/Socket';
import { useDispatch, useSelector } from 'react-redux';
import Private from './Private';
import PrivatePlan from './PrivatePlan';
import { isPermissionWithCompanyType } from 'hooks/useCompanyType';
import { isBrand } from 'hooks/useBrandType';
import { IS_LOGIN_USER, MODIFIED_LOG_OUT } from 'constants/user';
import {
  isPosProviderConnected,
  isTableOrderingProvider,
} from 'utils/checkAccessDenied';
import usePermission from '../hooks/permission';
import * as all from 'constants/permissions';
import SkeletonSidebarList, {
  SkeletonWrap,
} from './Settings/Skeleton/SkeletonSidebarList';
import usePermissionPlan from 'hooks/permissionPlan';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';
import useSidebarPages from 'hooks/useSidebarPages';
import { useTranslation } from 'react-i18next';
import { confirm } from './MenuNew/ConfirmModal';
import { ListItemButton, Collapse } from '@mui/material';

// Material UI Icons
import HomeIcon from '@mui/icons-material/Home';
// import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import LanguageIcon from '@mui/icons-material/Language';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from 'assets/icons/inventory.png';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const excludePages = ['/website/main', '/website/coupons', '/providers', '/'];
const excludePagesBrand = [
  '/website/main',
  '/website/coupons',
  '/providers',
  '/reports',
  '/orders',
  '/orders/history',
];

// The standalone "Inventory Management" menu section is hidden for now (code kept
// intentionally). Its Vendors and Purchase Invoices entries live under the
// existing "Inventory" accordion instead; their routes remain registered.
const SHOW_INVENTORY_MANAGEMENT = false;

// Profit IQ's in-page tabs live under /profit-iq, and two of them collide with
// the substring checks other items use to decide selection: /profit-iq/settings
// contains '/settings' and /profit-iq/menu-items contains '/menu'.
const isProfitIqPath = (pathname) => pathname.startsWith('/profit-iq');

// Paths that belong to the existing "Inventory" accordion. This is the old
// inventory area (/inventory/*, excluding /inventory-management) plus the
// inventory-management sections that were relocated under it.
const isOldInventoryPath = (pathname) =>
  (pathname.startsWith('/inventory') &&
    !pathname.startsWith('/inventory-management')) ||
  pathname.startsWith('/inventory-management/vendors') ||
  pathname.startsWith('/inventory-management/purchase-invoices') ||
  pathname.startsWith('/inventory-management/import');

const Sidebar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const planId = searchParams.get('plan');
  const corporateBusiness = searchParams.get('corporateBusiness');
  const checkPermission = usePermission();
  const checkPermissionPlan = usePermissionPlan();
  const { data, isLogin, modifiedLogOut } = useSelector(({ user }) => user);
  const { currentBusiness } = useSelector(({ business }) => business);
  const providers = useSelector(({ providers }) => providers);
  const { newOrders } = useSelector(({ orders }) => orders);
  const { isTypeSingle } = isPermissionWithCompanyType();
  const { isTypeBrand } = isBrand();
  const { eventEmit } = useSocket();
  const [clickLogOut, setClickLogOut] = useState(false);
  const [inventoryExpanded, setInventoryExpanded] = useState(
    isOldInventoryPath(location.pathname)
  );
  const [inventoryMgmtExpanded, setInventoryMgmtExpanded] = useState(
    location.pathname.startsWith('/inventory-management')
  );
  const inventoryMgmtPages = [
    { label: 'Inventory Dashboard', path: '/inventory-management/dashboard' },
    { label: 'Inventory Products', path: '/inventory-management/products' },
    { label: 'Inventory Lots', path: '/inventory-management/inventory-lots' },
    { label: 'Vendors', path: '/inventory-management/vendors' },
    {
      label: 'Purchase Invoices',
      path: '/inventory-management/purchase-invoices',
    },
    { label: 'Warehouses', path: '/inventory-management/warehouses' },
    { label: 'Stock Transfers', path: '/inventory-management/stock-transfers' },
    {
      label: 'Stock Adjustments',
      path: '/inventory-management/stock-adjustments',
    },
    { label: 'Reservations', path: '/inventory-management/stock-reservations' },
    {
      label: 'Inventory Counts',
      path: '/inventory-management/inventory-counts',
    },
    { label: 'Inventory Reports', path: '/inventory-management/reports' },
    { label: 'Alerts', path: '/inventory-management/alerts' },
    { label: 'Inventory Settings', path: '/inventory-management/settings' },
  ];
  const [posMenuExpanded, setPosMenuExpanded] = useState(
    location.pathname.startsWith('/pos-menu')
  );
  const [posReportsExpanded, setPosReportsExpanded] = useState(
    location.pathname.startsWith('/pos-reports')
  );
  const isInventoryStore = currentBusiness?.businessTypes?.includes(
    BUSINESS_TYPE_CATEGORY.inventoryStore
  );
  const isRestaurantWithNestedMenu = currentBusiness?.businessTypes?.includes(
    BUSINESS_TYPE_CATEGORY.restaurantWithNestedMenu
  );

  const providerCheck =
    window.location.pathname.includes('/providers') &&
    checkPermissionPlan(all['APP_BUNDLE']) &&
    checkPermission(all['PROVIDERS_VIEW']) &&
    isTypeSingle
      ? !providers?.items?.length
      : 0;

  const skeletonLoading = !currentBusiness?.roles || !data || providerCheck;

  // Use custom hook for sidebar pages
  const { availableSidebarPages } = useSidebarPages();

  useLayoutEffect(() => {
    if (
      availableSidebarPages?.length === 3 &&
      location.pathname !== '/help' &&
      location.pathname !== '/schedule' &&
      !location.pathname.includes('/schedule/new') &&
      !location.pathname.includes('/menu')
    ) {
      history.push(availableSidebarPages[0]?.path || '/menu/overview');
    }
  }, [availableSidebarPages, location.pathname, history]);

  const handleLogout = useCallback(async () => {
    dispatch({ type: MODIFIED_LOG_OUT, payload: true });
    setClickLogOut(true);
    history.push({
      pathname: history.location.pathname,
      state: {
        logOut: true,
      },
    });
  }, [dispatch, history]);

  const openConfirmPopup = useCallback(async () => {
    if (clickLogOut && modifiedLogOut !== 'opened' && modifiedLogOut) {
      const confirmed = await confirm({
        title: t('providers_settings.are_you_sure'),
        message: t('providers_settings.do_you_logout'),
        cancelText: t('orders.cancel'),
        confirmText: t('providers_settings.yes_logout'),
        confirmColor: 'primary',
      });
      if (confirmed) {
        eventEmit('leave', data?._id);
        dispatch(logout()).then(() => {
          history.push('/login');
          setClickLogOut(false);
          dispatch({ type: MODIFIED_LOG_OUT, payload: false });
        });
      } else {
        setClickLogOut(false);
      }
    }
  }, [clickLogOut, modifiedLogOut, t, eventEmit, data, history]);

  useEffect(() => {
    openConfirmPopup();
  }, [openConfirmPopup]);

  // Auto-expand the existing "Inventory" accordion for any of its paths
  // (including the relocated Vendors / Purchase Invoices sections).
  useEffect(() => {
    setInventoryExpanded(isOldInventoryPath(location.pathname));
    setInventoryMgmtExpanded(
      location.pathname.startsWith('/inventory-management')
    );
  }, [location.pathname]);

  // Auto-expand POS reports menu when on POS reports pages
  useEffect(() => {
    if (location.pathname.startsWith('/pos-reports')) {
      setPosReportsExpanded(true);
    } else {
      setPosReportsExpanded(false);
    }
  }, [location.pathname]);

  const isTableOrdering = isTableOrderingProvider(providers?.items);
  const isPosConnected = isPosProviderConnected(providers?.items);
  const promotionsEnabled = usePromotionsEnabled();

  useEffect(() => {
    if (!location.pathname.includes('history')) {
      dispatch({
        type: ORDERS_HISTORY,
        payload: {
          data: {
            count: 0,
            orders: [],
          },
        },
      });
    }
  }, [location]);

  useEffect(() => {
    if (token && planId && corporateBusiness) {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        history.push(`/providers?token=${token}`);
      }
    }
  }, [token, planId, corporateBusiness]);

  useEffect(() => {
    if (window.location.pathname.includes('history')) {
      return;
    }
    if (currentBusiness && Object.keys(currentBusiness).length > 0) {
      if (!isTypeSingle && !isTypeBrand && isLogin) {
        history.push('/orders');
        dispatch({ type: IS_LOGIN_USER, payload: false });
      } else if (
        !isTypeSingle &&
        !isTypeBrand &&
        !isLogin &&
        excludePages.includes(window.location.pathname)
      ) {
        history.push('/orders');
      } else if (isTypeSingle && isLogin) {
        const redirectUrl = localStorage.getItem('redirectUrl');
        if (redirectUrl) {
          history.push(redirectUrl);
          return;
        }
        if (!checkPermission(all['HOME_REPORT_FILTER'])) {
          history.push('/orders');
        } else {
          history.push('/');
        }
        dispatch({ type: IS_LOGIN_USER, payload: false });
      } else if (isTypeSingle && !isLogin) {
        if (
          !checkPermission(all['HOME_REPORT_FILTER']) &&
          window.location.pathname === '/'
        ) {
          history.push('/orders');
        }
        if (
          !checkPermission(all['TUTORIAL_VIEW']) &&
          window.location.pathname === '/tutorials'
        ) {
          history.push('/orders');
        }
        if (
          (!checkPermission(all['APP_BUNDLE']) &&
            !window.location.search.includes('restaurantmailbox')) ||
          (!checkPermission(all['PROVIDERS_VIEW']) &&
            window.location.pathname === '/providers') ||
          (!checkPermission(all['PROVIDERS_VIEW']) &&
            window.location.pathname === '/providers')
        ) {
          history.push('/orders');
        }
        if (
          !checkPermission(all['SETTINGS_WEBSITE']) &&
          window.location.pathname === '/website/coupons'
        ) {
          history.push('/orders');
        }
        if (
          !checkPermission(all['SETTINGS_WEBSITE']) &&
          window.location.pathname === '/website/main'
        ) {
          history.push('/orders');
        }
        if (
          !checkPermission(all['WEB_BUNDLE']) &&
          window.location.pathname === '/website/coupons'
        ) {
          history.push('/orders');
        }
        if (
          !isInventoryStore &&
          window.location.pathname.includes('/inventory')
        ) {
          history.push('/orders');
        }
      } else if (!isTypeSingle && isTypeBrand && isLogin) {
        history.push('/menu/overview');
        dispatch({ type: IS_LOGIN_USER, payload: false });
      } else if (
        !isTypeSingle &&
        isTypeBrand &&
        !isLogin &&
        excludePagesBrand.includes(window.location.pathname)
      ) {
        history.push('/menu/overview');
      } else if (
        !isInventoryStore &&
        window.location.pathname.includes('/inventory')
      ) {
        history.push('/orders');
      }
    }
  }, [
    isTypeSingle,
    isTypeBrand,
    currentBusiness,
    isLogin,
    history,
    dispatch,
    checkPermission,
    all,
    excludePages,
    excludePagesBrand,
    isInventoryStore,
  ]);

  return (
    <>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Optional Header with Back Arrow */}
        {/* Uncomment the following block if you want to add a back arrow at the top of the Sidebar */}
        {/* 
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #ccc',
          }}
        >
          <IconButton onClick={() => history.goBack()} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ marginLeft: '8px' }}>
            {t('sidebar.navigation')}
          </Typography>
        </Box>
        */}

        <Box className="sidebar-body">
          {skeletonLoading ? (
            <SkeletonSidebarList />
          ) : (
            <List
              disablePadding={true}
              sx={{
                '& .Mui-selected': {
                  backgroundColor: '#222933 !important',
                },
              }}
            >
              {isTypeSingle && (
                <Private permission="HOME_REPORT_FILTER">
                  <ListItemButton
                    selected={location.pathname === '/'}
                    component={Link}
                    to="/"
                  >
                    <ListItemIcon>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Home')} />
                  </ListItemButton>
                </Private>
              )}

              {!isTypeBrand && (
                <Private permission="ORDER_VIEW">
                  <ListItemButton
                    selected={location.pathname.includes('orders')}
                    onClick={() => dispatch({ type: RESET_ORDERS_PAGINATION })}
                    component={Link}
                    to="/orders"
                  >
                    <ListItemIcon>
                      <ListAltIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Orders')}>
                      {!!newOrders && (
                        <Badge pill color="danger" className="ml-auto">
                          {newOrders}
                        </Badge>
                      )}
                    </ListItemText>
                  </ListItemButton>
                </Private>
              )}
              {checkPermission(all['SUPER']) ||
              !checkPermissionPlan(all['EXTERNAL_BUNDLE']) ? (
                <Private permission="SETTINGS_USERS">
                  <ListItemButton
                    selected={location.pathname.includes('chargeback')}
                    onClick={() => dispatch({ type: RESET_ORDERS_PAGINATION })}
                    component={Link}
                    to="/chargeback/new"
                  >
                    <ListItemIcon>
                      <MoneyOffIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Chargeback')} />
                  </ListItemButton>
                </Private>
              ) : (
                <></>
              )}
              {!isTypeBrand && (
                <Private permission="ORDER_VIEW">
                  <ListItemButton
                    selected={location.pathname.includes('history')}
                    onClick={() => dispatch({ type: RESET_ORDERS_PAGINATION })}
                    component={Link}
                    to="/history"
                  >
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Order history')}>
                      {!!newOrders && (
                        <Badge pill color="danger" className="ml-auto">
                          {newOrders}
                        </Badge>
                      )}
                    </ListItemText>
                  </ListItemButton>
                </Private>
              )}

              <Private permission="MENU_VIEW">
                <ListItemButton
                  selected={
                    location.pathname.includes('/menu') &&
                    !location.pathname.includes('/pos-menu') &&
                    !isProfitIqPath(location.pathname)
                  }
                  component={Link}
                  to="/menu/overview"
                >
                  <ListItemIcon>
                    <RestaurantMenuIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('menu.name')} />
                </ListItemButton>
              </Private>

              {isPosConnected && isRestaurantWithNestedMenu && (
                <>
                  <ListItemButton
                    selected={location.pathname.startsWith('/pos-menu')}
                    onClick={() => {
                      setPosMenuExpanded(!posMenuExpanded);
                    }}
                  >
                    <ListItemIcon>
                      <MenuBookIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('POS Menu')} />
                    {posMenuExpanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </ListItemButton>
                  <Collapse in={posMenuExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        backgroundColor: '#222933 !important',
                        '& .Mui-selected': {
                          backgroundColor: '#191F27 !important',
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft:
                            location.pathname === '/pos-menu/overview'
                              ? '4px solid #FF8A00'
                              : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname === '/pos-menu/overview'}
                        component={Link}
                        to="/pos-menu/overview"
                      >
                        <ListItemText primary={t('Overview')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft:
                            location.pathname === '/pos-menu/preview'
                              ? '4px solid #FF8A00'
                              : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname === '/pos-menu/preview'}
                        component={Link}
                        to="/pos-menu/preview"
                      >
                        <ListItemText primary={t('Preview')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft: location.pathname.startsWith(
                            '/pos-menu/items'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-menu/items'
                        )}
                        component={Link}
                        to="/pos-menu/items"
                      >
                        <ListItemText primary={t('Items')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft: location.pathname.startsWith(
                            '/pos-menu/modifier-groups'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-menu/modifier-groups'
                        )}
                        component={Link}
                        to="/pos-menu/modifier-groups"
                      >
                        <ListItemText primary={t('Modifier Groups')} />
                      </ListItemButton>
                      {checkPermission(all['SUPER']) && (
                        <ListItemButton
                          sx={{
                            pl: 9,
                            borderLeft:
                              location.pathname === '/pos-menu/import'
                                ? '4px solid #FF8A00'
                                : 'none',
                            boxSizing: 'content-box',
                          }}
                          selected={location.pathname === '/pos-menu/import'}
                          component={Link}
                          to="/pos-menu/import"
                        >
                          <ListItemText primary={t('Import Menu')} />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </>
              )}

              {isInventoryStore && (
                <Private permission="MENU_EDIT">
                  <ListItemButton
                    selected={isOldInventoryPath(location.pathname)}
                    onClick={() => setInventoryExpanded(!inventoryExpanded)}
                    sx={
                      {
                        // backgroundColor: '#222933 !important',
                      }
                    }
                  >
                    <ListItemIcon>
                      <img
                        src={InventoryIcon}
                        alt="Inventory"
                        width="24"
                        height="24"
                      />
                    </ListItemIcon>
                    <ListItemText primary={t('Inventory')} />
                    {inventoryExpanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </ListItemButton>
                  <Collapse in={inventoryExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        backgroundColor: '#222933 !important',
                        '& .Mui-selected': {
                          backgroundColor: '#191F27 !important',
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft: location.pathname.startsWith(
                            '/inventory/items'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname.startsWith(
                          '/inventory/items'
                        )}
                        component={Link}
                        to="/inventory/items"
                      >
                        <ListItemText primary={t('Items')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/inventory/categories'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/inventory/categories'
                        )}
                        component={Link}
                        to="/inventory/categories"
                      >
                        <ListItemText primary={t('Categories')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/inventory-management/vendors'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/inventory-management/vendors'
                        )}
                        component={Link}
                        to="/inventory-management/vendors"
                      >
                        <ListItemText primary={t('Vendors')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/inventory-management/purchase-invoices'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/inventory-management/purchase-invoices'
                        )}
                        component={Link}
                        to="/inventory-management/purchase-invoices"
                      >
                        <ListItemText primary={t('Purchase Invoices')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/inventory-management/import'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/inventory-management/import'
                        )}
                        component={Link}
                        to="/inventory-management/import"
                      >
                        <ListItemText primary={t('Import')} />
                      </ListItemButton>
                    </List>
                  </Collapse>
                </Private>
              )}

              {SHOW_INVENTORY_MANAGEMENT && (
                <Private permission="MENU_EDIT">
                  <ListItemButton
                    selected={location.pathname.startsWith(
                      '/inventory-management'
                    )}
                    onClick={() => {
                      setInventoryMgmtExpanded(!inventoryMgmtExpanded);
                      if (!inventoryMgmtExpanded) {
                        history.push('/inventory-management/dashboard');
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Inventory2OutlinedIcon sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText primary={t('Inventory Management')} />
                    {inventoryMgmtExpanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={inventoryMgmtExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        backgroundColor: '#222933 !important',
                        '& .Mui-selected': {
                          backgroundColor: '#191F27 !important',
                        },
                      }}
                    >
                      {inventoryMgmtPages.map((page) => (
                        <ListItemButton
                          key={page.path}
                          sx={{
                            pl: 9,
                            boxSizing: 'content-box',
                            borderLeft: location.pathname.startsWith(page.path)
                              ? '4px solid #FF8A00'
                              : 'none',
                          }}
                          selected={location.pathname.startsWith(page.path)}
                          component={Link}
                          to={page.path}
                        >
                          <ListItemText primary={t(page.label)} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Private>
              )}

              {isTableOrdering && (
                <Private permission="SETTINGS_USERS">
                  <ListItemButton
                    selected={location.pathname.includes('/table-ordering')}
                    component={Link}
                    to="/table-ordering/tables"
                  >
                    <ListItemIcon>
                      <TableRestaurantIcon />
                    </ListItemIcon>
                    <ListItemText primary="Table Ordering" />
                  </ListItemButton>
                </Private>
              )}
              {isPosConnected && (
                <Private permission="SETTINGS_USERS">
                  <ListItemButton
                    selected={location.pathname.includes('/table-setup')}
                    component={Link}
                    to="/table-setup/tables"
                  >
                    <ListItemIcon>
                      <TableRestaurantIcon />
                    </ListItemIcon>
                    <ListItemText primary="Table Setup" />
                  </ListItemButton>
                </Private>
              )}

              {!isTypeBrand && (
                <Private permission="HOME_REPORT_FILTER">
                  <ListItemButton
                    selected={location.pathname.startsWith('/reports')}
                    component={Link}
                    to="/reports"
                  >
                    <ListItemIcon>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.reports')} />
                  </ListItemButton>
                </Private>
              )}

              {isTypeSingle && (
                <PrivatePlan permission="APP_BUNDLE">
                  <Private permission="PROVIDERS_VIEW">
                    <ListItemButton
                      selected={location.pathname.includes('/providers')}
                      component={Link}
                      to="/providers"
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('Providers')} />
                    </ListItemButton>
                  </Private>
                </PrivatePlan>
              )}
              {isPosConnected && (
                <Private permission="HOME_REPORT_FILTER">
                  <ListItemButton
                    selected={location.pathname.startsWith('/pos-reports')}
                    onClick={() => {
                      setPosReportsExpanded(!posReportsExpanded);
                      if (!posReportsExpanded) {
                        history.push('/pos-reports/summary');
                      }
                    }}
                  >
                    <ListItemIcon>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Pos Reports (Beta)')} />
                    {posReportsExpanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={posReportsExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        backgroundColor: '#222933 !important',
                        '& .Mui-selected': {
                          backgroundColor: '#191F27 !important',
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{
                          pl: 9,
                          borderLeft: location.pathname.startsWith(
                            '/pos-reports/summary'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                          boxSizing: 'content-box',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-reports/summary'
                        )}
                        component={Link}
                        to="/pos-reports/summary"
                      >
                        <ListItemText primary={t('Summary')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/pos-reports/delivery'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-reports/delivery'
                        )}
                        component={Link}
                        to="/pos-reports/delivery"
                      >
                        <ListItemText primary={t('Delivery Report (Beta)')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/pos-reports/top-sellers'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-reports/top-sellers'
                        )}
                        component={Link}
                        to="/pos-reports/top-sellers"
                      >
                        <ListItemText primary={t('Top Seller Items')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/pos-reports/sales-by-category'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-reports/sales-by-category'
                        )}
                        component={Link}
                        to="/pos-reports/sales-by-category"
                      >
                        <ListItemText primary={t('Sales by Category')} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          pl: 9,
                          boxSizing: 'content-box',
                          borderLeft: location.pathname.startsWith(
                            '/pos-reports/inventory-damage'
                          )
                            ? '4px solid #FF8A00'
                            : 'none',
                        }}
                        selected={location.pathname.startsWith(
                          '/pos-reports/inventory-damage'
                        )}
                        component={Link}
                        to="/pos-reports/inventory-damage"
                      >
                        <ListItemText primary={t('Inventory Damage')} />
                      </ListItemButton>
                      {promotionsEnabled && (
                        <ListItemButton
                          sx={{
                            pl: 9,
                            boxSizing: 'content-box',
                            borderLeft: location.pathname.startsWith(
                              '/pos-reports/promotions'
                            )
                              ? '4px solid #FF8A00'
                              : 'none',
                          }}
                          selected={location.pathname.startsWith(
                            '/pos-reports/promotions'
                          )}
                          component={Link}
                          to="/pos-reports/promotions"
                        >
                          <ListItemText primary={t('Promotion Report')} />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </Private>
              )}

              {promotionsEnabled && (
                <ListItemButton
                  selected={location.pathname.startsWith('/promotions')}
                  component={Link}
                  to="/promotions"
                >
                  <ListItemIcon>
                    <LocalOfferIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('Promotions')} />
                </ListItemButton>
              )}

              {/* Flat rather than an accordion: the seven Profit IQ views are
                  tabs inside the page, so the sidebar has one destination. */}
              <Private permission="PROFIT_IQ_VIEW">
                <ListItemButton
                  selected={location.pathname.startsWith('/profit-iq')}
                  component={Link}
                  to="/profit-iq/overview"
                >
                  <ListItemIcon>
                    <InsightsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('profit_iq.name')} />
                </ListItemButton>
              </Private>

              {(checkPermission(all['SUPER']) ||
                !checkPermissionPlan(all['EXTERNAL_BUNDLE'])) &&
                !isTypeBrand && (
                  <PrivatePlan permission="WEB_BUNDLE">
                    <Private permission="SETTINGS_WEBSITE">
                      <ListItemButton
                        selected={location.pathname.includes('/website')}
                        component={Link}
                        to="/website/main"
                      >
                        <ListItemIcon>
                          <LanguageIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('menu.website')} />
                      </ListItemButton>
                    </Private>
                  </PrivatePlan>
                )}
              {(checkPermission(all['SUPER']) ||
                !checkPermissionPlan(all['EXTERNAL_BUNDLE'])) &&
                !isTypeBrand && (
                  <PrivatePlan permission="WEB_BUNDLE">
                    <Private permission="SETTINGS_WEBSITE">
                      <ListItemButton
                        selected={location.pathname.includes(
                          '/delivery-service'
                        )}
                        component={Link}
                        to="/delivery-service"
                      >
                        <ListItemIcon>
                          <LocalShippingIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('delivery_service_tab')} />
                      </ListItemButton>
                    </Private>
                  </PrivatePlan>
                )}
              <Private permission="ORDER_VIEW">
                <ListItemButton
                  selected={
                    location.pathname.includes('/settings') &&
                    !location.pathname.includes('table-ordering') &&
                    !location.pathname.includes('/website') &&
                    !location.pathname.includes('/providers') &&
                    !location.pathname.includes('/providers') &&
                    !isProfitIqPath(location.pathname)
                  }
                  component={Link}
                  to="/settings/business"
                >
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('sidebar.settings')} />
                </ListItemButton>
              </Private>

              {/*<Private permission="TUTORIAL_VIEW">*/}
              {/*  <ListItemButton*/}
              {/*    selected={location.pathname.includes('/tutorials')}*/}
              {/*    component={Link}*/}
              {/*    to="/tutorials"*/}
              {/*  >*/}
              {/*    <ListItemIcon>*/}
              {/*      <BookIcon />*/}
              {/*    </ListItemIcon>*/}
              {/*    <ListItemText primary={t('sidebar.tutorials')} />*/}
              {/*  </ListItemButton>*/}
              {/*</Private>*/}

              <Private permission="HELP_VIEW">
                <ListItemButton
                  selected={location.pathname.includes('/help')}
                  component={Link}
                  to="/help"
                >
                  <ListItemIcon>
                    <HelpOutlineIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('help.name')} />
                </ListItemButton>
              </Private>

              <Private permission="SCHEDULE_READ">
                <ListItemButton
                  selected={location.pathname.includes('/schedule')}
                  component={Link}
                  to="/schedule"
                >
                  <ListItemIcon>
                    <AccessTimeIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('schedule.name')} />
                </ListItemButton>
              </Private>
            </List>
          )}
        </Box>

        {/* Logout Section */}
        <Box className={'logout-block'} sx={{ width: '100%' }}>
          {skeletonLoading ? (
            <>
              <SkeletonWrap variant="text" height={5} width={239} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: 2,
                  pr: 2,
                  height: '81px',
                }}
              >
                <Box sx={{ minWidth: '56px' }}>
                  <SkeletonWrap variant="rounded" width={20} height={20} />
                </Box>
                <SkeletonWrap variant="rounded" width={151} height={24} />
              </Box>
            </>
          ) : (
            <ListItemButton
              onClick={handleLogout}
              sx={{
                py: 3,
                borderTop: `1px solid #646973`,
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('sidebar.log_Out')} />
            </ListItemButton>
          )}
        </Box>
      </Box>
    </>
  );
};

export default React.memo(Sidebar);
